// scheduleBuild.js — turn parsed/created visits into the slot-based schedule
// the auto-switcher and vertical timeline run on (V0.5 MultiRecord++).
//
// Slot shape (matches liveStore.schedule / the Pulse):
//   { slotId, start: Date, end: Date,
//     patients: [{ patientName, conversationId, group }] }
//
// Visits sharing a start time become one concurrent slot (a 2:1 / 3:1).
// Visits with no stated time are sequenced after the timed ones at their
// stated/default duration, starting from the next half hour.
import { parseBackendDateMs } from './time';

const nextHalfHour = (fromMs) => {
  const d = new Date(fromMs);
  d.setSeconds(0, 0);
  const m = d.getMinutes();
  d.setMinutes(m <= 30 ? 30 : 60);
  if (m > 30) d.setMinutes(0); // rolled to next hour
  return d.getTime();
};

// Lenient time parse -> ms (today). Accepts "HH:MM" (24h), "H:MM AM/PM",
// "10:30a", "2pm-ish" forms. Returns null if unparseable.
const timeToMsToday = (time, baseMs) => {
  if (!time || typeof time !== 'string') return null;
  const m = time.trim().match(/^(\d{1,2}):(\d{2})\s*([ap]m?)?$/i);
  if (!m) return null;
  let h = Number(m[1]);
  const min = Number(m[2]);
  const ap = m[3] ? m[3][0].toLowerCase() : null;
  if (ap === 'p' && h < 12) h += 12;
  if (ap === 'a' && h === 12) h = 0;
  const d = new Date(baseMs);
  d.setHours(h, min, 0, 0);
  return d.getTime();
};

/**
 * @param created  - [{ visit: {patientName,time,durationMin,config}, conversationId }]
 * @param sessions - liveStore sessions (to backfill conversationId/group by name)
 * @param nowMs    - clock (injected for testability; defaults to Date.now())
 */
export const buildScheduleFromVisits = (created, sessions = [], nowMs) => {
  const now = nowMs || Date.now();
  const byName = new Map(
    (sessions || []).map((s) => [(s.patientName || s.name || '').trim(), s]),
  );

  // Resolve each visit's conversationId/group (prefer the create response,
  // fall back to a freshly-hydrated session matched by name).
  const resolved = (created || []).map(({ visit, conversationId, group }) => {
    const session = byName.get((visit.patientName || '').trim());
    return {
      patientName: visit.patientName,
      conversationId: conversationId || session?.conversationId || null,
      group: group || session?.group || null,
      time: visit.time || null,
      durationMin: visit.durationMin || 30,
    };
  });

  // Group by start time; null-time visits get sequenced afterward.
  const timed = resolved.filter((v) => timeToMsToday(v.time, now) != null);
  const untimed = resolved.filter((v) => timeToMsToday(v.time, now) == null);

  const slotsByStart = new Map();
  const pushSlot = (startMs, durationMin, v) => {
    const key = startMs;
    if (!slotsByStart.has(key)) {
      slotsByStart.set(key, {
        slotId: `mr_${startMs}`,
        start: new Date(startMs),
        end: new Date(startMs + durationMin * 60 * 1000),
        patients: [],
      });
    }
    const slot = slotsByStart.get(key);
    // Extend the slot end to the longest visit in it.
    const end = startMs + durationMin * 60 * 1000;
    if (end > slot.end.getTime()) slot.end = new Date(end);
    slot.patients.push({
      patientName: v.patientName,
      conversationId: v.conversationId,
      group: v.group,
      end: new Date(end),
    });
  };

  timed.forEach((v) => pushSlot(timeToMsToday(v.time, now), v.durationMin, v));

  // Sequence untimed visits after the last timed slot (or from the next
  // half hour), back-to-back at their duration.
  let cursor = nextHalfHour(now);
  for (const slot of slotsByStart.values()) {
    cursor = Math.max(cursor, slot.end.getTime());
  }
  untimed.forEach((v) => {
    pushSlot(cursor, v.durationMin, v);
    cursor += v.durationMin * 60 * 1000;
  });

  return [...slotsByStart.values()].sort(
    (a, b) => a.start.getTime() - b.start.getTime(),
  );
};

const DEFAULT_TAIL_MS = 30 * 60 * 1000; // assumed length of the last visit
// Cap the inferred length at 60 min — matches the timeline card's MAX_DUR_MIN,
// so the RECORDING WINDOW can't outrun what the card shows. (Previously 2h: a
// sparse schedule made a 6pm visit's window stretch to the next visit hours
// later, so it stayed "recording" long after its card ended.)
const MAX_VISIT_MS = 60 * 60 * 1000;

/**
 * The schedule derived straight from the loaded conversations (yesterday +
 * today — the timeline's day stepper picks which day to show). The backend
 * stores no scheduled time or duration, so we anchor each visit at its
 * `createdAt` and infer its length by stretching to the NEXT visit's start,
 * CAPPED at MAX_VISIT_MS (60 min) so a long gap never makes a visit's card or
 * recording window run for hours. The last visit gets a 30-min tail. This
 * is the source of truth post-create and across reloads — typed loader times
 * are not persisted.
 *
 * @param sessions  - liveStore sessions (need createdAt + conversationId)
 * @param overrides - {conversationId: {start, end}} pinned windows (win over
 *                    createdAt). Legacy plain-number entries = start only.
 */
export const buildScheduleFromSessions = (sessions = [], overrides = {}) => {
  // Normalize an override to {start?, end?} (older builds stored a bare ms).
  const win = (ov) => {
    if (ov && typeof ov === 'object') return ov;
    if (Number.isFinite(ov)) return { start: ov };
    return {};
  };
  const items = (sessions || [])
    .filter((s) => s.createdAt && s.conversationId != null)
    .map((s) => {
      const ov = win(overrides[String(s.conversationId)]);
      const scheduledStart = parseBackendDateMs(s.scheduledStart);
      const scheduledEnd = parseBackendDateMs(s.scheduledEnd);
      const overrideStart = parseBackendDateMs(ov.start);
      const overrideEnd = parseBackendDateMs(ov.end);
      const createdStart = parseBackendDateMs(s.createdAtMs ?? s.createdAt);
      // Anchor priority: backend scheduledStart -> local pinned override ->
      // createdAt. Same for the end (scheduledEnd -> override.end -> inferred).
      const start = Number.isFinite(scheduledStart)
        ? scheduledStart
        : Number.isFinite(overrideStart)
          ? overrideStart
          : createdStart;
      const pinnedEnd = Number.isFinite(scheduledEnd)
        ? scheduledEnd
        : Number.isFinite(overrideEnd)
          ? overrideEnd
          : null;
      return { s, start, pinnedEnd };
    })
    .filter((x) => Number.isFinite(x.start))
    .sort((a, b) => a.start - b.start);

  const groups = [];
  for (const item of items) {
    const prev = groups[groups.length - 1];
    if (prev && prev.start === item.start) {
      prev.items.push(item);
    } else {
      groups.push({ start: item.start, items: [item] });
    }
  }

  return groups.map((group, i) => {
    const next = groups[i + 1];
    // Explicit end (drawn duration) wins; otherwise infer by stretching to the
    // next visit's start, capped, with a 30-min tail for the last.
    const explicitEnd = Math.max(
      ...group.items
        .map((x) => x.pinnedEnd)
        .filter((end) => Number.isFinite(end)),
    );
    let endMs = Number.isFinite(explicitEnd)
      ? explicitEnd
      : next
        ? Math.min(next.start, group.start + MAX_VISIT_MS)
        : group.start + DEFAULT_TAIL_MS;
    if (endMs <= group.start) endMs = group.start + DEFAULT_TAIL_MS;
    return {
      slotId: `mr_${group.start}`,
      start: new Date(group.start),
      end: new Date(endMs),
      patients: group.items.map((x) => ({
        patientName: x.s.patientName,
        conversationId: x.s.conversationId,
        group: x.s.group,
        end: new Date(
          Number.isFinite(x.pinnedEnd) && x.pinnedEnd > group.start
            ? x.pinnedEnd
            : endMs,
        ),
      })),
    };
  });
};

export default buildScheduleFromVisits;
